import React, { FunctionComponent, ReactElement } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import Card from '@/src/components/Card';
import { RatingReview } from '@/interfaces/Rating';
import { RatingIcons } from '@/src/components/icons';

const RatingDescription = styled.article`
  padding: 0.5rem 1rem 1.5rem;
`;

const RatingTitle = styled.span`
  font-weight: bold;
  margin: 0 0 0.5rem;
  display: block;
`;

const ReviewTags = styled.div`
  margin-top: 0.5rem;
`;

const RatingHeadlineWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  padding: 1rem 1rem 0.5rem;
`;

const RatingHeadlineStars = styled(RatingIcons)`
  width: 6.25rem;
`;

const RatingAuthorAndDate = styled.div`
  margin-top: 0.25rem;
  color: #a0a0a0;
`;

interface RatingPreviewCardProps {
  rating: RatingReview;
}

const RatingPreviewHeadline: FunctionComponent<RatingPreviewCardProps> = ({ rating }) => {
  const {
    customerRatings: { iconType },
  } = useConfig();
  return (
    <RatingHeadlineWrapper>
      <div>
        <span data-testid="rating-card-headline-stars">
          <RatingHeadlineStars
            maskWidth={`${parseFloat(rating.rating.toFixed(1)) * 20}%`}
            iconType={iconType}
          />
        </span>
        <RatingAuthorAndDate data-testid="rating-card-author-and-date">
          <FormattedMessage
            id="rating.author"
            defaultMessage="<nickname>von {authorNickName}</nickname> | {created}"
            values={{
              authorLocation: rating.reviewer && rating.reviewer.location,
              authorNickName: rating.reviewer && rating.reviewer.nickname,
              created: rating.created,
              nickname: (txt) => (rating.reviewer && rating.reviewer.nickname ? txt : null),
            }}
          />
        </RatingAuthorAndDate>
      </div>
    </RatingHeadlineWrapper>
  );
};

export const RatingPreviewCard: FunctionComponent<RatingPreviewCardProps> = ({
  rating,
}): ReactElement => (
  <div data-testid="rating-preview-card">
    <Card headline={<RatingPreviewHeadline rating={rating} />}>
      <RatingDescription>
        {rating.title && rating.title !== '' && (
          <RatingTitle data-testid="rating-card-title">{rating.title}</RatingTitle>
        )}
        <div data-testid="rating-review-description">{rating.description}</div>
        {rating.reviewTags && rating.reviewTags.proTags && (
          <ReviewTags data-testid="rating-card-pro-tags">
            <strong>
              <FormattedMessage id="rating.labels.pro" defaultMessage="Vorteile: " />
            </strong>
            {rating.reviewTags.proTags.map((tag, index) =>
              index < rating.reviewTags.proTags.length - 1 ? `${tag.name}, ` : tag.name,
            )}
          </ReviewTags>
        )}
        {rating.reviewTags && rating.reviewTags.contraTags && (
          <ReviewTags data-testid="rating-card-contra-tags">
            <strong>
              <FormattedMessage id="rating.labels.contra" defaultMessage="Nachteile: " />
            </strong>
            {rating.reviewTags.contraTags.map((tag, index) =>
              index < rating.reviewTags.contraTags.length - 1 ? `${tag.name}, ` : tag.name,
            )}
          </ReviewTags>
        )}
      </RatingDescription>
    </Card>
  </div>
);

export default RatingPreviewCard;
